import { useMemo } from "react"
import { UnitType } from "@/types/warehouse"
import { UnitTypeItem } from "./unit-type-item"
import { Badge } from "../ui/badge"
import { RadioGroup } from "@/components/ui/radio-group"
import { cn, formatSurface } from "@/lib/utils"
import { MESSENGERS } from "@/config/contacts"
import { useUnitTypesShowcasePrices } from "@/hooks/prices/useUnitTypesShowcasePrices"

export type UnitTypeGroupsProps = {
  facilityCode?: string | null
  unitTypes: UnitType[]
  occupiedUnitTypes?: UnitType[]
  value: string | null
  onValueChange: (code: string) => void
}

export function UnitTypeGroups({
  facilityCode,
  unitTypes,
  occupiedUnitTypes = [],
  value,
  onValueChange,
}: UnitTypeGroupsProps) {
  const unitTypeMedium = unitTypes.filter(unitType => unitType.surface < 5)
  const unitTypeLarge = unitTypes.filter(unitType => unitType.surface >= 5)
  const uniqueOccupiedUnitTypes = [
    ...new Map(occupiedUnitTypes.map((unitType) => [unitType.surface, unitType])).values(),
  ].sort((a, b) => a.surface - b.surface)

  const productCodes = useMemo(
    () => unitTypes.map((unitType) => unitType.code),
    [unitTypes],
  )

  const { pricesByCode, isLoading: isPricesLoading } = useUnitTypesShowcasePrices(
    facilityCode,
    productCodes,
  )

  return (
    <div
      className={cn(
        "gap-8 lg:p-8 p-2 pt-6 pb-2",
        "flex h-auto flex-col",
        "lg:grid lg:min-h-full lg:grid-rows-[1fr_auto]",
      )}
    >
      <RadioGroup value={value ?? ""} onValueChange={onValueChange} className="flex flex-col gap-8">
        {unitTypeMedium.length > 0 && (
          <div>
            <h2 className="text-[18px] leading-[24px] font-medium mb-3">Стандартные</h2>
            <div className="grid xl:grid-cols-4 lg:grid-cols-3 grid-cols-2 lg:gap-2 gap-1">
              {unitTypeMedium.map(unitType => (
                <UnitTypeItem
                  key={unitType.code}
                  unitType={unitType}
                  monthlyPrice={pricesByCode.get(unitType.code)?.monthly?.total}
                  isPriceLoading={isPricesLoading && !pricesByCode.has(unitType.code)}
                />
              ))}
            </div>
          </div>
        )}
        {unitTypeLarge.length > 0 && (
          <div>
            <h2 className="text-[18px] leading-[24px] font-medium mb-3">Большие</h2>
            <div className="grid xl:grid-cols-4 lg:grid-cols-3 grid-cols-2 lg:gap-2 gap-1">
              {unitTypeLarge.map(unitType => (
                <UnitTypeItem
                  key={unitType.code}
                  unitType={unitType}
                  monthlyPrice={pricesByCode.get(unitType.code)?.monthly?.total}
                  isPriceLoading={isPricesLoading && !pricesByCode.has(unitType.code)}
                />
              ))}
            </div>
          </div>
        )}
      </RadioGroup>

      {occupiedUnitTypes.length > 0 && (
        <div className="flex flex-col gap-4 align-self-end">
          <span className="text-[18px] leading-[24px] font-medium text-foreground">
            Не нашли подходящий размер?
          </span>
          <div className="flex flex-wrap gap-2">
            {uniqueOccupiedUnitTypes.map((unitType) => (
              <Badge
                key={unitType.surface}
                className="text-[18px] leading-[18px] font-medium text-light-muted bg-background p-1 rounded-[2px] h-auto"
              >
                {formatSurface(unitType.surface)}
              </Badge>
            ))}
          </div>
          <div className="text-[18px] leading-[24px] font-medium text-light-muted">
            На этом складе есть боксы и других размеров.<br />
            В данный момент они заняты, но вы можете встать в лист ожидания.<br />
            Позвоните нам — мы знаем когда освободится бокс.<br />
            <a href="tel:+78122202649" className="text-brand">
              +7 (812) 220-26-49
            </a>{" "}
            или напишите в мессенджере —{" "}
            {MESSENGERS.filter((chat) => chat.name !== "MAX").map((chat, index) => (
              <span key={chat.name}>
                {index > 0 ? ", " : null}
                <a href={chat.href} target="_blank" rel="noopener noreferrer" className="text-brand">
                  {chat.name}
                </a>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
